import { firstToLowerCase } from '@kros-sk/utils-fn/string';

import type { ObjectPath, PathValue } from './object-path.types';

export type Constructor<T> = new (...args: any[]) => T;

/**
 * Array like filter for objects. Remove properties from object that do not fulfill filter function
 * @param o
 * @param filterFn Filter function.
 * @returns Object with properties that fulfill filter function.
 */
export function objectFilter<T extends object>(o: T, filterFn: (value: any, key: string) => boolean): Partial<T> {
  const entries = Object.entries(o);
  const filtered = entries.filter(([key, value]) => filterFn(value, key));
  return Object.fromEntries(filtered) as Partial<T>;
}

/**
 * Checks if value is an Object.
 * @param value Value to check if is an Object.
 * @returns True if value is an Object, otherwise false.
 */
export function isObject(value: any): boolean {
  if (value === null) return false;
  return typeof value === 'function' || typeof value === 'object';
}

/**
 * Checks if the given value is an object record.
 * An object record is an object that is neither null nor an array.
 * @param value The value to check.
 * @returns True if the value is an object record, false otherwise.
 */
export function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Checks if object is empty.
 * @param obj Object to check if is empty.
 * @returns True if object is empty, otherwise false.
 */
export function isObjectEmpty(obj: object): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Compares two objects.
 * @param obj1 First object to compare.
 * @param obj2 Second object to compare.
 * @returns True if objects are equal, otherwise false.
 */
export function compareObjects(obj1: any, obj2: any): boolean {
  // just one of them is undefined
  if (obj1 === undefined && obj2 !== undefined) return false;
  if (obj1 !== undefined && obj2 === undefined) return false;

  // just one of them is null
  if (obj1 === null && obj2 !== null) return false;
  if (obj1 !== null && obj2 === null) return false;

  // both are objects
  if (obj1 !== null && typeof obj1 === 'object' && obj2 !== null && typeof obj2 === 'object') {
    return JSON.stringify(obj1, getSortedObjectKeys(obj1)) === JSON.stringify(obj2, getSortedObjectKeys(obj2));
  }

  // all other
  return obj1 === obj2;
}

/**
 * Gets sorted keys (property names) of provided object.
 * @param obj A source object to get keys from.
 * @returns Sorted keys of provided object.
 */
export function getSortedObjectKeys(obj: object): string[] {
  const ret = new Set<string>();
  JSON.stringify(obj, (k, v) => {
    ret.add(k);
    return v;
  });
  return Array.from(ret).sort();
}

/**
 * Creates generic object from JSON string with lowercase keys.
 * @param jsonString JSON string to parse to object.
 * @returns Object with lowercase keys.
 */
export function parseObjectWithLowercaseKeys<T>(jsonString: string): T {
  const obj = JSON.parse(jsonString);
  const result: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = key.charAt(0).toLowerCase() + key.slice(1);
      const value = obj[key];

      if (Array.isArray(value)) {
        result[newKey] = value.map((item) => {
          if (typeof item === 'object' && item !== null) {
            return parseObjectWithLowercaseKeys(JSON.stringify(item));
          } else {
            return item;
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        result[newKey] = parseObjectWithLowercaseKeys(JSON.stringify(value));
      } else {
        result[newKey] = value;
      }
    }
  }

  return result as T;
}

/**
 * Converts first letter of all object keys to lowercase.
 * @param obj Object whose keys should be converted.
 * @returns New object with same values but keys starting with lowercase.
 */
export function objectKeysFirstToLower(obj: Record<string, any>): Record<string, any> {
  const changes: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    changes[firstToLowerCase(key)] = obj[key];
  });

  return changes;
}

/**
 * Reads nested value from object by dot separated path.
 * Example: `resolveObjectValueByPath(data, 'payload.documentNumber')`.
 *
 * @param data Source object.
 * @param path Dot separated path.
 * @returns Resolved value or `undefined` when any path segment is missing.
 */
export function resolveObjectValueByPath<TData extends Record<string, any>, TPath extends ObjectPath<TData>>(
  data: TData | null | undefined,
  path: TPath
): PathValue<TData, TPath> | undefined {
  let current: any = data;

  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }

  return current as PathValue<TData, TPath> | undefined;
}
